import type {
  CompanyType,
  IntelCompaniesSelectDto,
  IntelCompaniesSortBy,
  IntelCompanyDetailDto,
  SortDirection,
} from '@@types/api.types';
import { ApiErrorWrapper, useApi } from '@hooks/api.hook.tsx';
import { queryKeys } from '@queries/keys.ts';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

type Nullable<T> = T | null | undefined;

export type IntelOrganizationsQueryParams = {
  enabled?: boolean;
  limit?: Nullable<number>;
  page?: Nullable<number>;
  search?: Nullable<string>;
  sortBy?: Nullable<IntelCompaniesSortBy>;
  sortDirection?: Nullable<SortDirection>;
  type?: Nullable<CompanyType>;
};

const buildQueryParams = (params: IntelOrganizationsQueryParams) => {
  const queryParams: Record<string, unknown> = {};

  if (params.limit) {
    queryParams.limit = params.limit;
  }

  if (params.page) {
    queryParams.page = params.page;
  }

  if (params.search) {
    queryParams.search = params.search;
  }

  if (params.sortBy) {
    queryParams.sortBy = params.sortBy;
  }

  if (params.sortDirection) {
    queryParams.sortDirection = params.sortDirection;
  }

  if (params.type) {
    queryParams.type = params.type;
  }

  return queryParams;
};

export const useIntelOrganizationQuery = (params: { companyId?: string; enabled?: boolean }) => {
  const { apiGet } = useApi();
  const { companyId, enabled = true } = params;
  const queryEnabled = Boolean(enabled && companyId);

  return useQuery<IntelCompanyDetailDto, ApiErrorWrapper>({
    enabled: queryEnabled,
    queryFn: async () => {
      return await apiGet<IntelCompanyDetailDto>({
        path: `/v1/intel/organizations/${companyId}`,
      });
    },
    queryKey: queryEnabled ? queryKeys.intelOrganizations.getOrganization(companyId) : queryKeys.placeholders.default,
    refetchOnWindowFocus: true,
  });
};

export const useIntelOrganizationsQuery = (params: IntelOrganizationsQueryParams) => {
  const { apiGet } = useApi();
  const { enabled = true } = params;
  const queryParams = buildQueryParams(params);

  return useQuery<IntelCompaniesSelectDto, ApiErrorWrapper>({
    enabled,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      return await apiGet<IntelCompaniesSelectDto>({
        path: '/v1/intel/organizations',
        query: queryParams,
      });
    },
    queryKey: queryKeys.intelOrganizations.getOrganizations(queryParams),
    refetchOnWindowFocus: true,
  });
};
